import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsPage, SettingsRow, SettingsRowList, SettingsStateLine } from '../SettingsPage';
import { PublishingCard } from '../drives/PublishingCard';
import { AddIntegrationMenu } from './AddIntegrationMenu';
import { getIntegrationKind } from './integration-kinds';
import { STATUS_BADGE_VARIANT, useIntegrationRows } from './integration-rows';

type IntegrationsIndexProps = {
  memberId: number;
  isHubAdmin: boolean;
};

export function IntegrationsIndex({ memberId, isHubAdmin }: IntegrationsIndexProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { rows, isLoading, isError, error } = useIntegrationRows(memberId);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => {
      const definition = getIntegrationKind(row.kind);
      return (
        row.name.toLowerCase().includes(term) ||
        row.detail.toLowerCase().includes(term) ||
        definition.label.toLowerCase().includes(term) ||
        definition.vendor.toLowerCase().includes(term)
      );
    });
  }, [rows, search]);

  return (
    <SettingsPage
      title="Integrations"
      description="Everything you and this Hub have connected for Neo to work with."
      actions={
        <>
          <div className="relative w-[240px] sm:w-[280px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search integrations..."
              aria-label="Search integrations"
              className="h-9 pl-10"
            />
          </div>
          <AddIntegrationMenu memberId={memberId} isHubAdmin={isHubAdmin} rows={rows} />
        </>
      }
    >
      <PublishingCard memberId={memberId} isHubAdmin={isHubAdmin} />
      {isLoading ? <SettingsStateLine>Loading integrations...</SettingsStateLine> : null}
      {isError ? (
        <SettingsStateLine tone="destructive">
          {getAxiosErrorDisplayMessage(error, 'Failed to load integrations.')}
        </SettingsStateLine>
      ) : null}
      {!isLoading && !isError && filteredRows.length === 0 ? (
        <SettingsStateLine>
          {search.trim()
            ? 'No integrations match your search.'
            : 'Nothing connected yet. Use Add to connect an account, a file source, Xero or a tool server.'}
        </SettingsStateLine>
      ) : null}

      {!isLoading && !isError && filteredRows.length > 0 ? (
        <SettingsRowList>
          {filteredRows.map((row) => {
            const definition = getIntegrationKind(row.kind);
            return (
              <SettingsRow
                key={row.key}
                truncate
                onSelect={() => navigate(row.href)}
                selectLabel={`Open ${row.name}`}
                leading={<img src={definition.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />}
                title={row.name}
                meta={
                  <>
                    <Badge variant="outline">{definition.label}</Badge>
                    <Badge variant="secondary">{row.scope === 'member' ? 'You' : 'Hub'}</Badge>
                  </>
                }
                detail={row.detail}
                trailing={<Badge variant={STATUS_BADGE_VARIANT[row.status.tone]}>{row.status.label}</Badge>}
              />
            );
          })}
        </SettingsRowList>
      ) : null}
    </SettingsPage>
  );
}
